import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, clearAuth, getUser } from "@/lib/api";
import { getActiveLearner, setActiveLearner } from "@/lib/learner";
import { useLiveSync } from "@/lib/useLiveSync";
import LearnerPicker from "@/components/LearnerPicker";
import { Card } from "@/components/ui/card";
import Logo from "@/components/Logo";
import { BookOpen, LogOut, ArrowRight, Eye, UserCog, Headphones, Volume2, Trophy, Sparkles, MessageSquare, RotateCcw, MessageCircleHeart, Lock as LockIcon, CheckCircle2, ScrollText, FileLock2, PlayCircle, Award } from "lucide-react";
import DemoBanner from "@/components/DemoBanner";
import ManifestoGate from "@/components/ManifestoGate";
import DoctrineAccessModal from "@/components/DoctrineAccessModal";
import WalkthroughModal, { useFirstVisitWalkthrough } from "@/components/WalkthroughModal";

// Rotating, time-aware welcome messages. Acoustic-flavoured puns, first-name aware.
const GREETING_MESSAGES = [
  { emoji: "🎧", line: "Ready to tune your ears today?" },
  { emoji: "🔊", line: "Make some sound decisions — let's go." },
  { emoji: "✨", line: "Every module you finish is one less excuse on Monday." },
  { emoji: "🎯", line: "Knowledge compounds quietly — like good acoustics." },
  { emoji: "🚀", line: "10 minutes today beats 2 hours next week." },
  { emoji: "🏆", line: "Future-you is already proud of this session." },
  { emoji: "🧠", line: "Learn it once, sell it forever." },
  { emoji: "🎓", line: "Five questions stand between you and a certificate." },
  { emoji: "🪄", line: "Plot twist: the quiz is easier than you think." },
  { emoji: "💡", line: "Brilliant ideas live in finished modules." },
  { emoji: "🎶", line: "Sound is architecture — and so is your growth." },
  { emoji: "⚡", line: "The fastest sales reps are the most curious ones." },
];
const TIME_GREETING = () => {
  const h = new Date().getHours();
  if (h < 5) return "Burning the midnight oil";
  if (h < 12) return "Good morning";
  if (h < 17) return "Good afternoon";
  if (h < 21) return "Good evening";
  return "Good night";
};
const pickGreeting = (seed = 0) => GREETING_MESSAGES[(Date.now() + seed) % GREETING_MESSAGES.length];

export default function LearnerHome() {
  const [courses, setCourses] = useState([]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [greetIdx, setGreetIdx] = useState(0);
  const [resume, setResume] = useState(null);
  const [startedMap, setStartedMap] = useState({});
  // course_id → boolean from /reading-progress/me/resume. Used to badge
  // each curriculum tile as "Completed ✓" and swap the CTA to "Read again".
  const [courseCompletion, setCourseCompletion] = useState({});
  const [feedbackElig, setFeedbackElig] = useState(null);
  // Program certificate rollup — drives the locked/unlocked "Certificate"
  // header tab and the claim banner. null until fetched.
  const [certStatus, setCertStatus] = useState(null);
  // `coursesLoading` distinguishes "still fetching" from "fetched and empty",
  // so we don't flash "No published courses yet" before the API even returns.
  // `coursesError` lets us show a Retry CTA on a real failure instead of
  // silently leaving an empty state (the root cause of the "no courses on
  // first login, refresh 3-4× to see them" complaint).
  const [coursesLoading, setCoursesLoading] = useState(true);
  const [coursesError, setCoursesError] = useState(false);
  // Structured error metadata so the UI can show *what* went wrong
  // (timeout / 5xx / 401 / offline) instead of the generic "network blip"
  // message that hid backend concurrency stalls from us for weeks.
  const [coursesErrorInfo, setCoursesErrorInfo] = useState(null);
  // First-visit walkthrough modal (auto-opens once, then learners use the
  // "Walkthrough" header button to replay it). Persisted via localStorage.
  const [walkthroughOpen, setWalkthroughOpen] = useFirstVisitWalkthrough();
  // Controls the "Only for Design & Development" access modal — opens
  // when the second special tile is clicked.
  const [doctrineOpen, setDoctrineOpen] = useState(false);
  // Memo so the user identity is stable across renders — without this, the
  // effect on line ~64 spins forever (getUser() returns a fresh object every
  // render, blowing up dep equality).
  const user = useMemo(getUser, []);
  const isDealer = user?.audience === "dealer";
  const [learner, setLearner] = useState(() => {
    const existing = getActiveLearner();
    if (existing) return existing;
    // If logged in via OTP, the JWT already carries learner identity — auto-attach.
    if (user?.role === "learner" && user?.learner_id) {
      const auto = { id: user.learner_id, name: user.name, email: user.email, department: "" };
      setActiveLearner(auto);
      return auto;
    }
    return null;
  });
  const nav = useNavigate();

  // Dealer onboarding — temporary password must be changed before browsing.
  useEffect(() => {
    if (user?.must_reset_password) nav("/reset-password", { replace: true });
  }, [user, nav]);

  const reloadCourses = useCallback(async () => {
    setCoursesError(false);
    setCoursesErrorInfo(null);
    // Up to 3 attempts with exponential backoff (700 ms, 1.4 s). Guards
    // against transient stalls on a single-worker async backend where one
    // user's bcrypt/LLM call briefly starves another user's request.
    let lastErr = null;
    for (let attempt = 0; attempt < 3; attempt += 1) {
      try {
        const { data } = await api.get("/courses");
        setCourses(Array.isArray(data) ? data : []);
        setCoursesError(false);
        setCoursesErrorInfo(null);
        setCoursesLoading(false);
        return;
      } catch (e) {
        lastErr = e;
        if (attempt < 2) await new Promise((r) => setTimeout(r, 700 * 2 ** attempt));
      }
    }
    if (lastErr) {
      // Classify the failure so the UI can show something actionable.
      // axios sets `code: "ECONNABORTED"` on timeout; offline → no response;
      // 5xx → backend hiccup; 401 → token revoked.
      const status = lastErr?.response?.status;
      const code = lastErr?.code;
      let kind = "network";
      let message = "Network unreachable. Check your connection and try again.";
      if (code === "ECONNABORTED" || /timeout/i.test(lastErr?.message || "")) {
        kind = "timeout";
        message = "The server took too long to respond. This is usually load — please retry.";
      } else if (status === 401) {
        kind = "auth";
        message = "Your session expired. Please sign in again.";
      } else if (status === 429) {
        kind = "rate";
        message = "Too many requests right now. Wait a few seconds, then retry.";
      } else if (status >= 500) {
        kind = "server";
        message = "Our learning servers are briefly unreachable. We've already tried a few times — please tap Retry, or come back in a minute or two if it persists.";
      } else if (status) {
        kind = "http";
        message = `Request failed with HTTP ${status}. Please retry.`;
      }
      // eslint-disable-next-line no-console
      console.warn("Courses fetch failed after retries", { status, code, message });
      setCoursesError(true);
      setCoursesErrorInfo({ kind, status, message });
      setCoursesLoading(false);
    }
  }, []);

  useEffect(() => {
    // Admin previewers still get the picker; OTP learners are pre-identified.
    if (!learner && user?.role !== "learner") setPickerOpen(true);
    reloadCourses();
    // Fetch resume pointer + per-module progress rollup. Soft-fail if endpoint
    // isn't deployed yet (older production builds).
    api.get("/reading-progress/me/resume")
      .then(({ data }) => {
        setResume(data?.resume || null);
        const map = {};
        (data?.started_modules || []).forEach((m) => { map[m.module_id] = m; });
        setStartedMap(map);
        setCourseCompletion(data?.course_completion || {});
      })
      .catch(() => {});
    api.get("/feedback/eligibility")
      .then(({ data }) => setFeedbackElig(data))
      .catch(() => {});
    if (learner?.name) {
      api.get("/program-certificate/eligibility", { params: { learner_name: learner.name } })
        .then(({ data }) => setCertStatus(data))
        .catch(() => {});
    }
  }, [learner, user, reloadCourses]);

  // Live sync: refetch when any admin changes a course (create / delete /
  // publish toggle / rename).
  useLiveSync({ kind: "courses", onUpdate: reloadCourses });

  // Rotate greeting every 5s for a touch of life on the hero strip.
  useEffect(() => {
    const t = setInterval(() => setGreetIdx((i) => (i + 1) % GREETING_MESSAGES.length), 5000);
    return () => clearInterval(t);
  }, []);

  const switchLearner = () => {
    // Admins can swap to preview as another roster learner. Real learners must sign out.
    if (user?.role === "learner") {
      clearAuth();
      setActiveLearner(null);
      nav("/login");
      return;
    }
    setActiveLearner(null);
    setLearner(null);
    setPickerOpen(true);
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Founder Manifesto blocking gate — checks itself on mount and renders
          the full-screen modal only when the learner needs to acknowledge.
          Acknowledgements expire after 90 days (server-side). */}
      {!isDealer && <ManifestoGate />}
      {/* Doctrine modal — controlled by `doctrineOpen` below. Two-step: name +
          department audit form → PDF viewer. */}
      <DoctrineAccessModal open={doctrineOpen} onClose={() => setDoctrineOpen(false)} />
      {/* Auto-opens once on a learner's first visit; replayable via the
          "Walkthrough" button in the header. */}
      <WalkthroughModal open={walkthroughOpen} onClose={() => setWalkthroughOpen(false)} />
      <DemoBanner />
      {/* Top bar */}
      <header className="border-b border-slate-200 bg-white">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <Logo className="h-10" />
          <div className="flex items-center gap-4">
            {!isDealer && (
            <Link
              to="/learn/forum"
              data-testid="forum-nav-link"
              className="hidden sm:inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-[#E53935] transition-colors"
            >
              <MessageSquare size={14}/> Forum
            </Link>
            )}
            {certStatus?.eligible || certStatus?.certificate ? (
              <Link
                to="/learn/certificate"
                data-testid="certificate-nav-link"
                className="hidden sm:inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-[#E53935] transition-colors"
              >
                <Award size={14} className="text-[#E53935]"/> Certificate
              </Link>
            ) : certStatus ? (
              <span
                data-testid="certificate-nav-link-disabled"
                title={`Pass all module quizzes to unlock (${certStatus.passed_quizzes}/${certStatus.total_quizzes} passed)`}
                className="hidden sm:inline-flex items-center gap-1.5 text-sm text-slate-400 cursor-not-allowed"
              >
                <LockIcon size={12}/> Certificate
              </span>
            ) : null}
            {feedbackElig?.eligible || feedbackElig?.already_submitted ? (
              <Link
                to="/learn/feedback"
                data-testid="feedback-nav-link"
                className="hidden sm:inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-[#E53935] transition-colors"
              >
                {feedbackElig?.already_submitted
                  ? <><CheckCircle2 size={14} className="text-emerald-600"/> Feedback sent</>
                  : <><MessageCircleHeart size={14} className="text-[#E53935]"/> Share feedback</>}
              </Link>
            ) : feedbackElig ? (
              <span
                data-testid="feedback-nav-link-disabled"
                title={`Available after you've read all modules (${feedbackElig?.completed_modules || 0}/${feedbackElig?.total_modules || 0} done)`}
                className="hidden sm:inline-flex items-center gap-1.5 text-sm text-slate-400 cursor-not-allowed"
              >
                <LockIcon size={12}/> Feedback
              </span>
            ) : null}
            {learner && (
              <button
                data-testid="active-learner-chip"
                onClick={switchLearner}
                className="inline-flex items-center gap-2 px-3 py-1.5 bg-slate-50 border border-slate-200 text-slate-800 rounded-full text-xs font-medium hover:border-[#E53935] hover:text-[#E53935] transition-colors"
              >
                <span className="w-5 h-5 rounded-full bg-[#222222] text-white flex items-center justify-center text-[10px] font-bold">
                  {learner.name.charAt(0).toUpperCase()}
                </span>
                <span>{learner.name}</span>
                <UserCog size={11} />
              </button>
            )}
            {user?.role === "admin" && (
              <Link to="/admin" className="text-sm text-slate-600 hover:text-[#E53935] inline-flex items-center gap-1.5">
                <Eye size={14}/> Admin
              </Link>
            )}
            <button
              data-testid="open-walkthrough"
              onClick={() => setWalkthroughOpen(true)}
              className="text-sm text-slate-600 hover:text-[#E53935] inline-flex items-center gap-1.5"
              title="Replay the learner walkthrough"
            >
              <PlayCircle size={14}/> Walkthrough
            </button>
            <button data-testid="learner-logout" onClick={() => { clearAuth(); setActiveLearner(null); nav("/login"); }} className="text-sm text-slate-500 hover:text-red-600 flex items-center gap-1.5">
              <LogOut size={14}/> Sign out
            </button>
          </div>
        </div>
      </header>

      {/* HERO — academy branding */}
      <section className="relative overflow-hidden border-b border-slate-200">
        <div className="absolute inset-0 acoustic-rings opacity-40 pointer-events-none"/>
        <div className="absolute -top-32 -right-32 w-[500px] h-[500px] rounded-full border border-[#E53935]/10"/>
        <div className="absolute -top-16 -right-16 w-[300px] h-[300px] rounded-full border border-[#E53935]/15"/>

        <div className="relative max-w-6xl mx-auto px-6 py-14 md:py-20 grid md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-7">
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-white border border-slate-200 rounded-full text-[10px] uppercase tracking-[0.25em] text-slate-600 mb-5">
              <span className="w-1.5 h-1.5 bg-[#E53935] rounded-full live-pulse"/>
              Corporate L&amp;D · Sales Enablement
            </div>
            <h1 className="font-display font-extrabold text-4xl md:text-5xl lg:text-6xl leading-[1.02] tracking-tight text-[#222222]">
              UniVicoustic <br className="hidden md:block"/>
              <span className="text-[#E53935]">Learners</span> Academy
            </h1>
            <p className="mt-4 text-lg md:text-xl text-slate-700 font-display italic">
              The Quiet Salespersons&apos; Handbook
            </p>
            <p className="mt-6 text-slate-600 max-w-xl leading-relaxed">
              Master sound — and the language of selling it. Self-paced modules, interactive lessons, AI-built knowledge checks. Earn a certificate for every module you pass.
            </p>

            <div className="mt-8 flex flex-wrap items-center gap-3">
              {courses.length > 0 ? (
                <a href="#courses" className="inline-flex items-center gap-2 px-5 py-3 bg-[#222222] hover:bg-black text-white font-medium rounded-md transition-colors" data-testid="hero-start-cta">
                  Start learning <ArrowRight size={16}/>
                </a>
              ) : (
                <button onClick={() => setPickerOpen(true)} className="inline-flex items-center gap-2 px-5 py-3 bg-[#222222] hover:bg-black text-white font-medium rounded-md transition-colors">
                  Set your name <ArrowRight size={16}/>
                </button>
              )}
              <div className="inline-flex items-center gap-2 px-3 py-2 text-xs text-slate-500">
                <Trophy size={14} className="text-[#E53935]"/> Earn certificates
              </div>
            </div>
          </div>

          {/* Right column: feature pillars */}
          <div className="md:col-span-5 grid grid-cols-2 gap-3">
            <FeaturePill icon={Headphones} label="Sound Science" hue="black"/>
            <FeaturePill icon={Volume2} label="Application" hue="red"/>
            <FeaturePill icon={BookOpen} label="Brand DNA" hue="red"/>
            <FeaturePill icon={Trophy} label="Certified" hue="black"/>
          </div>
        </div>
      </section>

      {/* Course list */}
      <main id="courses" className="max-w-6xl mx-auto px-6 py-14">
        {/* Time-aware engaging welcome strip — rotates every 5s */}
        {learner && (() => {
          const g = GREETING_MESSAGES[greetIdx];
          const firstName = (learner.name || "").split(" ")[0] || learner.name;
          return (
            <div
              data-testid="welcome-strip"
              className="mb-8 relative overflow-hidden rounded-xl border border-[#E53935]/25 bg-gradient-to-r from-white via-[#fff5f5] to-white px-5 py-4 flex items-center gap-4"
            >
              <div className="absolute inset-y-0 left-0 w-1 bg-[#E53935]" />
              <div className="shrink-0 w-12 h-12 rounded-full bg-[#222222] text-white flex items-center justify-center text-2xl select-none">
                <span key={greetIdx} style={{ animation: "welcomeEmojiPop 0.6s ease-out" }}>{g.emoji}</span>
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[10px] uppercase tracking-[0.22em] text-[#E53935] font-semibold mb-0.5 flex items-center gap-1.5">
                  <Sparkles size={11}/> {TIME_GREETING()}, {firstName}
                </div>
                <div key={greetIdx} className="text-slate-800 font-medium text-base sm:text-lg leading-tight"
                     style={{ animation: "welcomeLineFade 0.6s ease-out" }}>
                  {g.line}
                </div>
              </div>
              <style>{`
                @keyframes welcomeEmojiPop { 0% { transform: scale(0.6) rotate(-15deg); opacity: 0; } 100% { transform: scale(1) rotate(0); opacity: 1; } }
                @keyframes welcomeLineFade { 0% { opacity: 0; transform: translateY(4px); } 100% { opacity: 1; transform: translateY(0); } }
              `}</style>
            </div>
          );
        })()}

        {/* Resume-where-you-left-off card — appears when learner has prior progress */}
        {resume && resume.module_id && resume.scroll_pct < 95 && (
          <Link
            to={`/learn/modules/${resume.module_id}`}
            data-testid="resume-card"
            className="block mb-8 group"
          >
            <div className="relative overflow-hidden rounded-xl border border-[#E53935]/30 bg-gradient-to-r from-[#222222] to-[#3a1212] text-white px-5 py-5 sm:py-6 transition-all hover:shadow-[0_18px_50px_-15px_rgba(229,57,53,0.5)]">
              <div className="absolute right-0 top-0 bottom-0 w-1/2 opacity-15 pointer-events-none"
                   style={{ backgroundImage: "radial-gradient(circle at top right, #E53935, transparent 60%)" }} />
              <div className="relative flex items-center gap-4">
                <div className="shrink-0 w-12 h-12 rounded-full bg-[#E53935] flex items-center justify-center">
                  <RotateCcw size={20} className="text-white"/>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] uppercase tracking-[0.22em] text-[#E53935] font-bold mb-1">
                    Pick up where you left off
                  </div>
                  <div className="text-base sm:text-lg font-semibold leading-tight truncate" data-testid="resume-module-title">
                    {resume.module_title || "Continue learning"}
                  </div>
                  {resume.course_title && (
                    <div className="text-[11px] text-white/60 mt-0.5 truncate">
                      {resume.course_title}
                    </div>
                  )}
                  <div className="mt-2 flex items-center gap-3">
                    <div className="flex-1 h-1.5 bg-white/15 rounded-full overflow-hidden max-w-xs">
                      <div
                        className="h-full bg-gradient-to-r from-[#E53935] to-[#ff8a78] transition-all"
                        style={{ width: `${Math.max(2, resume.scroll_pct)}%` }}
                        data-testid="resume-progress-bar"
                      />
                    </div>
                    <div className="text-[10px] text-white/70 font-mono shrink-0">
                      {Math.round(resume.scroll_pct)}% read
                      {resume.total_sections > 0 && ` · ${resume.completed_sections}/${resume.total_sections} sections`}
                    </div>
                  </div>
                </div>
                <div className="shrink-0 inline-flex items-center gap-1.5 bg-[#E53935] hover:bg-white hover:text-[#E53935] text-white px-4 py-2 rounded-full text-xs font-bold transition-colors"
                     data-testid="resume-continue-button">
                  Continue <ArrowRight size={13}/>
                </div>
              </div>
            </div>
          </Link>
        )}

        {/* Sales Simulator highlight banner */}
        <Link
          to="/learn/simulator"
          className="block mb-10 group"
          data-testid="simulator-cta"
        >
          <Card className="p-0 overflow-hidden border-[#E53935]/30 hover:border-[#E53935] hover:shadow-xl transition-all bg-gradient-to-r from-[#222222] to-[#1a1a1a]">
            <div className="flex items-center gap-5 p-6 relative">
              <div className="absolute inset-0 acoustic-rings opacity-20 pointer-events-none"/>
              <div className="shrink-0 w-14 h-14 rounded-full bg-[#E53935]/15 border border-[#E53935]/40 flex items-center justify-center text-[#E53935]">
                <Headphones size={26}/>
              </div>
              <div className="flex-1 relative">
                <div className="text-[10px] uppercase tracking-[0.25em] text-[#E53935] font-bold mb-1 flex items-center gap-2">
                  New · Live Practice
                  <span className="px-1.5 py-0.5 bg-[#E53935] text-white rounded text-[9px]">BETA</span>
                </div>
                <h3 className="font-display text-xl text-white mb-1">Sales Simulator</h3>
                <p className="text-sm text-white/70 max-w-xl">
                  Roleplay a real sales conversation with a CXO from hospitality, education or corporate.
                  Voice or text. Live coach panel guides every turn.
                </p>
              </div>
              <ArrowRight className="text-white group-hover:translate-x-1 transition-transform shrink-0" size={20}/>
            </div>
          </Card>
        </Link>

        {/* Program certificate banner — appears once every quiz is passed */}
        {(certStatus?.eligible || certStatus?.certificate) && (
          <Link
            to="/learn/certificate"
            className="block mb-10 group"
            data-testid="certificate-banner"
          >
            <Card className="p-0 overflow-hidden border border-[#E53935]/40 bg-gradient-to-r from-[#222222] to-[#3a1212] hover:shadow-xl transition-all">
              <div className="flex items-center gap-5 p-6 relative">
                <div className="absolute inset-0 acoustic-rings opacity-20 pointer-events-none"/>
                <div className="shrink-0 w-14 h-14 rounded-full bg-[#E53935] flex items-center justify-center text-white">
                  <Award size={26}/>
                </div>
                <div className="flex-1 relative">
                  <div className="text-[10px] uppercase tracking-[0.25em] text-[#E53935] font-bold mb-1 flex items-center gap-2">
                    {certStatus?.certificate ? "Certificate earned" : "Program complete"}
                    {!certStatus?.certificate && <span className="px-1.5 py-0.5 bg-[#E53935] text-white rounded text-[9px]">UNLOCKED</span>}
                  </div>
                  <h3 className="font-display text-xl text-white mb-1">
                    {certStatus?.certificate ? "View & share your certificate" : "Claim your program certificate"}
                  </h3>
                  <p className="text-sm text-white/70 max-w-xl">
                    {certStatus?.certificate
                      ? "Download the PDF or add it to your LinkedIn profile."
                      : "You've passed every module quiz — generate your official UniVicoustic Learning Academy certificate."}
                  </p>
                </div>
                <ArrowRight className="text-white group-hover:translate-x-1 transition-transform shrink-0" size={20}/>
              </div>
            </Card>
          </Link>
        )}


        {/* Feedback CTA — only when eligible (read every module) or already submitted */}
        {feedbackElig && (feedbackElig.eligible || feedbackElig.already_submitted) && (
          <Link
            to="/learn/feedback"
            className="block mb-10 group"
            data-testid="feedback-banner"
          >
            <Card className={`p-0 overflow-hidden border transition-all hover:shadow-xl ${
              feedbackElig.already_submitted
                ? "border-emerald-200 bg-gradient-to-r from-emerald-50 to-white"
                : "border-[#E53935]/30 bg-gradient-to-r from-white via-[#fff5f5] to-white hover:border-[#E53935]"
            }`}>
              <div className="flex items-center gap-5 p-6 relative">
                <div className={`shrink-0 w-14 h-14 rounded-full flex items-center justify-center ${
                  feedbackElig.already_submitted
                    ? "bg-emerald-100 text-emerald-700 border border-emerald-200"
                    : "bg-[#E53935]/10 text-[#E53935] border border-[#E53935]/30"
                }`}>
                  {feedbackElig.already_submitted ? <CheckCircle2 size={26}/> : <MessageCircleHeart size={26}/>}
                </div>
                <div className="flex-1 relative">
                  <div className={`text-[10px] uppercase tracking-[0.25em] font-bold mb-1 flex items-center gap-2 ${
                    feedbackElig.already_submitted ? "text-emerald-700" : "text-[#E53935]"
                  }`}>
                    {feedbackElig.already_submitted ? "Thank you" : "We need your honest take"}
                    {!feedbackElig.already_submitted && <span className="px-1.5 py-0.5 bg-[#E53935] text-white rounded text-[9px]">UNLOCKED</span>}
                  </div>
                  <h3 className="font-display text-xl text-[#222222] mb-1">
                    {feedbackElig.already_submitted ? "Your feedback is with the team" : "Help shape v2 of the academy"}
                  </h3>
                  <p className="text-sm text-slate-600 max-w-xl">
                    {feedbackElig.already_submitted
                      ? "Tap to review what you submitted. We're reading every word."
                      : "This is our first attempt at building a corporate L&D tool — module by module, tell us what worked and what didn't."}
                  </p>
                </div>
                <ArrowRight className="text-[#222222] group-hover:translate-x-1 transition-transform shrink-0" size={20}/>
              </div>
            </Card>
          </Link>
        )}

        <div className="flex items-end justify-between mb-8">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-[#E53935] font-semibold mb-2">Curriculum</div>
            <h2 className="text-3xl font-display font-bold text-[#222222]">Your courses</h2>
          </div>
          {learner && (
            <div className="text-sm text-slate-500">
              Welcome back, <strong className="text-slate-800">{learner.name}</strong>
            </div>
          )}
        </div>

        {coursesLoading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="courses-loading-skeleton">
            {[0, 1, 2, 3, 4, 5].map((i) => (
              <Card key={i} className="h-full p-0 border-slate-200 overflow-hidden">
                <div className="h-36 bg-slate-200 animate-pulse"/>
                <div className="p-5 space-y-3">
                  <div className="h-4 bg-slate-200 rounded animate-pulse w-3/4"/>
                  <div className="h-3 bg-slate-100 rounded animate-pulse w-full"/>
                  <div className="h-3 bg-slate-100 rounded animate-pulse w-5/6"/>
                </div>
              </Card>
            ))}
          </div>
        ) : coursesError ? (
          <Card className="p-12 text-center border-rose-200 bg-rose-50" data-testid="courses-error-state">
            <BookOpen size={36} className="mx-auto text-rose-400 mb-3" strokeWidth={1.3} />
            <div className="text-rose-900 font-medium mb-1" data-testid="courses-error-title">
              {coursesErrorInfo?.kind === "timeout" && "The server is taking too long"}
              {coursesErrorInfo?.kind === "auth" && "Your session has expired"}
              {coursesErrorInfo?.kind === "rate" && "You're sending requests too quickly"}
              {coursesErrorInfo?.kind === "server" && "We can't reach the learning server right now"}
              {coursesErrorInfo?.kind === "http" && "Couldn't load your courses"}
              {(!coursesErrorInfo || coursesErrorInfo?.kind === "network") && "We can't reach the server"}
            </div>
            <div className="text-sm text-rose-700 mb-4" data-testid="courses-error-detail">
              {coursesErrorInfo?.message || "Tap to try again."}
              {coursesErrorInfo?.status ? (
                <span className="text-rose-400 ml-1" data-testid="courses-error-status">
                  (HTTP {coursesErrorInfo.status})
                </span>
              ) : null}
            </div>
            <button
              onClick={coursesErrorInfo?.kind === "auth" ? () => nav("/login") : reloadCourses}
              data-testid="courses-retry-button"
              className="inline-flex items-center gap-1.5 bg-[#E53935] hover:bg-[#c62828] text-white text-sm font-semibold px-4 py-2 rounded-full transition-colors"
            >
              {coursesErrorInfo?.kind === "auth" ? <LogOut size={14}/> : <RotateCcw size={14}/>}
              {coursesErrorInfo?.kind === "auth" ? "Sign in again" : "Retry"}
            </button>
          </Card>
        ) : courses.length === 0 ? (
          <Card className="p-16 text-center border-dashed" data-testid="courses-empty-state">
            <BookOpen size={40} className="mx-auto text-slate-300 mb-3" strokeWidth={1.3} />
            <div className="text-slate-700 font-medium">No published courses yet</div>
            <div className="text-sm text-slate-500 mt-1">Your administrator will publish courses here soon.</div>
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {/* ── First tile: "Believe Before We Sell" (login-gate doc) ──
                Clicking force-reopens the read-and-confirm gate via
                ?reread=manifesto. Hidden for dealer accounts. */}
            {!isDealer && (
            <button
              type="button"
              onClick={() => window.location.assign("/learn?reread=manifesto")}
              data-testid="tile-believe"
              className="text-left group focus:outline-none"
            >
              <Card className="h-full p-0 border-2 border-[#E53935]/30 hover:border-[#E53935] hover:shadow-xl transition-all overflow-hidden bg-gradient-to-br from-[#222222] to-[#1a1a1a] text-white">
                <div className="h-36 relative overflow-hidden">
                  <div className="absolute inset-0 acoustic-rings opacity-40" aria-hidden />
                  <div className="absolute top-3 left-4 text-white/80 text-[10px] uppercase tracking-[0.3em] font-medium">Required reading</div>
                  <div className="absolute bottom-3 left-4 right-4">
                    <div className="text-[10px] uppercase tracking-[0.25em] text-[#E53935] font-bold">UniVicoustic</div>
                  </div>
                  <div className="absolute bottom-3 right-4 w-10 h-10 rounded-full border-2 border-[#E53935] bg-black/35 backdrop-blur-sm flex items-center justify-center text-[#E53935]">
                    <ScrollText size={16} className="group-hover:scale-110 transition-transform"/>
                  </div>
                </div>
                <div className="p-5">
                  <h3 className="font-display font-semibold text-white mb-1 line-clamp-2 group-hover:text-[#E53935] transition-colors">
                    &ldquo;Believe Before We Sell&rdquo;
                  </h3>
                  <p className="text-sm text-slate-300 line-clamp-2 mb-4 min-h-[2.5rem]">
                    The Founders&rsquo; Manifesto — a message from the Managing Director.
                  </p>
                  <div className="flex items-center justify-between text-xs text-slate-400">
                    <span>Founders&rsquo; Manifesto</span>
                    <span className="text-[#E53935] font-semibold inline-flex items-center gap-1">
                      Open <ArrowRight size={11}/>
                    </span>
                  </div>
                </div>
              </Card>
            </button>
            )}

            {/* ── Second tile: original Founders' Manifesto letter ──────
                Opens the original manifesto PDF in a new tab (no gate).
                Hidden for dealer accounts. */}
            {!isDealer && (
            <button
              type="button"
              onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/gates/manifesto.pdf`, "_blank", "noopener")}
              data-testid="tile-manifesto"
              className="text-left group focus:outline-none"
            >
              <Card className="h-full p-0 border-2 border-[#E53935]/30 hover:border-[#E53935] hover:shadow-xl transition-all overflow-hidden bg-gradient-to-br from-[#222222] to-[#1a1a1a] text-white">
                <div className="h-36 relative overflow-hidden">
                  <div className="absolute inset-0 acoustic-rings opacity-40" aria-hidden />
                  <div className="absolute top-3 left-4 text-white/80 text-[10px] uppercase tracking-[0.3em] font-medium">Founding letter</div>
                  <div className="absolute bottom-3 left-4 right-4">
                    <div className="text-[10px] uppercase tracking-[0.25em] text-[#E53935] font-bold">UniVicoustic</div>
                  </div>
                  <div className="absolute bottom-3 right-4 w-10 h-10 rounded-full border-2 border-[#E53935] bg-black/35 backdrop-blur-sm flex items-center justify-center text-[#E53935]">
                    <ScrollText size={16} className="group-hover:scale-110 transition-transform"/>
                  </div>
                </div>
                <div className="p-5">
                  <h3 className="font-display font-semibold text-white mb-1 line-clamp-2 group-hover:text-[#E53935] transition-colors">
                    Founders&rsquo; Manifesto
                  </h3>
                  <p className="text-sm text-slate-300 line-clamp-2 mb-4 min-h-[2.5rem]">
                    The founding doctrine of UniVicoustic.
                  </p>
                  <div className="flex items-center justify-between text-xs text-slate-400">
                    <span>Founders&rsquo; Manifesto</span>
                    <span className="text-[#E53935] font-semibold inline-flex items-center gap-1">
                      Open <ArrowRight size={11}/>
                    </span>
                  </div>
                </div>
              </Card>
            </button>
            )}

            {courses.map((c, i) => (
              <Link key={c.id} to={`/learn/courses/${c.id}`} className="block group" data-testid={`learner-course-${c.id}`}>
                <Card className="h-full p-0 border-slate-200 group-hover:shadow-xl group-hover:border-[#E53935]/40 transition-all overflow-hidden">
                  <div className="h-36 bg-[#222222] relative overflow-hidden">
                    {/* Always show the acoustic-rings as a fallback layer behind
                        the cover image. If the cover momentarily fails to load
                        (lazy-load stutter, CORS preflight, slow CDN), the rings
                        show through instead of a hard black tile — matches the
                        no-cover state seamlessly. */}
                    <div className="absolute inset-0 acoustic-rings opacity-40" aria-hidden />
                    {c.cover_image ? (
                      <>
                        <img
                          src={c.cover_image}
                          alt=""
                          width="600"
                          height="240"
                          loading={i < 3 ? "eager" : "lazy"}
                          decoding="async"
                          fetchPriority={i === 0 ? "high" : "auto"}
                          data-testid={`course-cover-${c.id}`}
                          className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-500"
                          onError={(e) => { e.currentTarget.style.visibility = "hidden"; }}
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-[#222222]/70 via-[#222222]/15 to-[#222222]/40" />
                      </>
                    ) : null}
                    <div className="absolute top-3 left-4 text-white/80 text-[10px] uppercase tracking-[0.3em] font-medium drop-shadow">Module {String(i + 1).padStart(2, "0")}</div>
                    <div className="absolute bottom-3 left-4 right-4">
                      <div className="text-[10px] uppercase tracking-[0.25em] text-[#E53935] font-bold drop-shadow">UniVicoustic</div>
                    </div>
                    <div className="absolute bottom-3 right-4 w-10 h-10 rounded-full border-2 border-[#E53935] bg-[#222222]/35 backdrop-blur-sm flex items-center justify-center text-[#E53935]">
                      <ArrowRight size={16} className="group-hover:translate-x-0.5 transition-transform"/>
                    </div>
                  </div>
                  <div className="p-5">
                    <h3 className="font-display font-semibold text-[#222222] mb-1 line-clamp-2 group-hover:text-[#E53935] transition-colors">{c.title}</h3>
                    <p className="text-sm text-slate-500 line-clamp-2 mb-4 min-h-[2.5rem]">{c.description || "Start learning"}</p>
                    <div className="flex items-center justify-between text-xs text-slate-500">
                      {courseCompletion?.[c.id] ? (
                        <span className="inline-flex items-center gap-1 text-emerald-700 font-semibold" data-testid={`course-completed-badge-${c.id}`}>
                          <CheckCircle2 size={12}/> Completed
                        </span>
                      ) : (
                        <span>&nbsp;</span>
                      )}
                      <span className="text-[#E53935] font-semibold inline-flex items-center gap-1">
                        {courseCompletion?.[c.id] ? "Read again" : "Begin"} <ArrowRight size={11}/>
                      </span>
                    </div>
                  </div>
                </Card>
              </Link>
            ))}

            {/* ── Last tile: Design Doctrine ─────────────────────────────
                Sits at the end of the curriculum grid so it appears after
                the final module. Clicking opens the name+department audit
                form, then the PDF. Hidden for dealer accounts. */}
            {!isDealer && (
            <button
              type="button"
              onClick={() => setDoctrineOpen(true)}
              data-testid="tile-doctrine"
              className="text-left group focus:outline-none"
            >
              <Card className="h-full p-0 border-2 border-amber-300/50 hover:border-amber-500 hover:shadow-xl transition-all overflow-hidden bg-gradient-to-br from-amber-50 to-white">
                <div className="h-36 relative overflow-hidden bg-gradient-to-br from-amber-100 to-amber-50">
                  <div className="absolute inset-0 acoustic-rings opacity-20" aria-hidden />
                  <div className="absolute top-3 left-4 text-amber-900/70 text-[10px] uppercase tracking-[0.3em] font-medium">Restricted</div>
                  <div className="absolute bottom-3 left-4 right-4">
                    <div className="text-[10px] uppercase tracking-[0.25em] text-amber-900 font-bold">Internal use only</div>
                  </div>
                  <div className="absolute bottom-3 right-4 w-10 h-10 rounded-full border-2 border-amber-600 bg-white/60 backdrop-blur-sm flex items-center justify-center text-amber-700">
                    <FileLock2 size={16} className="group-hover:scale-110 transition-transform"/>
                  </div>
                </div>
                <div className="p-5">
                  <h3 className="font-display font-semibold text-[#222222] mb-1 line-clamp-2 group-hover:text-amber-700 transition-colors">
                    Only for Design &amp; Development team
                  </h3>
                  <p className="text-sm text-slate-600 line-clamp-2 mb-4 min-h-[2.5rem]">
                    UniVicoustic Product Design Doctrine. Access requires name + department for audit.
                  </p>
                  <div className="flex items-center justify-between text-xs text-slate-500">
                    <span>Design doctrine</span>
                    <span className="text-amber-700 font-semibold inline-flex items-center gap-1">
                      Open <ArrowRight size={11}/>
                    </span>
                  </div>
                </div>
              </Card>
            </button>
            )}
          </div>
        )}
      </main>

      <footer className="border-t border-slate-200 mt-10">
        <div className="max-w-6xl mx-auto px-6 py-6 flex items-center justify-between text-xs text-slate-500">
          <div className="inline-flex items-center gap-2">
            <Logo className="h-6" variant="mark"/>
            <span>UniVicoustic Learners Academy</span>
          </div>
          <div className="italic font-display">Makes sound sense.</div>
        </div>
      </footer>

      <LearnerPicker open={pickerOpen} onOpenChange={(v) => { if (!v && !learner) return; setPickerOpen(v); }} onPicked={setLearner} />
    </div>
  );
}

function FeaturePill({ icon: Icon, label, hue }) {
  const bg = hue === "red" ? "bg-[#E53935]/10 text-[#E53935] border-[#E53935]/30" : "bg-[#222222]/5 text-[#222222] border-[#222222]/20";
  return (
    <div className={`flex items-center gap-2.5 p-4 rounded-lg border ${bg}`}>
      <Icon size={18} strokeWidth={1.7}/>
      <span className="text-sm font-medium">{label}</span>
    </div>
  );
}
